import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { SemiCircleComponent } from '../../charts/semi-circle/semi-circle.component';

@Component({
  selector: 'app-generic-ai-visibility',
  standalone: true,
  imports: [CommonModule, MatAngularModule, SemiCircleComponent],
  templateUrl: './generic-ai-visibility.component.html',
  styleUrl: './generic-ai-visibility.component.scss'
})
export class GenericAiVisibilityComponent {
  @Input() llmComparisonData: any[] = [];
  @Input() llmComparisonMetricsProgress: any = null; // TaskStatus from backend
  @Input() isLLMComparisonMetricsLoading: boolean = false;
  @Input() llmComparisonMetricsTaskId: string | null = null; // Task ID for cancellation
  @Input() showReloadProgress: boolean = true; // When false (reload clicked), hide progress details below header
  /** Show/hide the inline AI Visibility progress block. Overview uses shared header loader instead. */
  @Input() showInlineProgress = true;
  /** When true, hide the refresh/reload AI Visibility metrics button (e.g. Overview tab). */
  @Input() hideReloadButton = false;
  @Output() filterChange = new EventEmitter<'all' | 'chatgpt' | 'gemini' | 'ai_overview' | 'ai_mode'>();
  @Output() cancelTask = new EventEmitter<string>(); // Emit task ID to cancel
  @Output() reloadMetrics = new EventEmitter<void>(); // Emit to trigger reload

  getChatGPTMetric(metric: 'ai_visibility' | 'mentions' | 'cited_pages' | 'monthly_audience' | 'industry_average'): number {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return 0;
    }
    const primaryBrand = this.llmComparisonData[0];
    
    // Use ai_visibility_metrics.platform_breakdown.chatgpt (new format)
    const chatgptData = primaryBrand?.ai_visibility_metrics?.platform_breakdown?.chatgpt;
    if (chatgptData !== undefined) {
      // Handle case where chatgpt is just a number (mentions count - legacy format)
      if (typeof chatgptData === 'number') {
        return metric === 'mentions' ? chatgptData : 0;
      }
      // Handle case where chatgpt is an object (new format)
      if (metric === 'ai_visibility') {
        return chatgptData.ai_visibility || 0;
      }
      return chatgptData[metric] || 0;
    }
    
    return 0;
  }

  getGeminiMetric(metric: 'ai_visibility' | 'mentions' | 'cited_pages' | 'monthly_audience' | 'industry_average'): number {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return 0;
    }
    const primaryBrand = this.llmComparisonData[0];
    
    // Use ai_visibility_metrics.platform_breakdown.gemini (new format)
    const geminiData = primaryBrand?.ai_visibility_metrics?.platform_breakdown?.gemini;
    if (geminiData !== undefined) {
      // Handle case where gemini is just a number (mentions count - legacy format)
      if (typeof geminiData === 'number') {
        return metric === 'mentions' ? geminiData : 0;
      }
      // Handle case where gemini is an object (new format)
      if (metric === 'ai_visibility') {
        return geminiData.ai_visibility || 0;
      }
      return geminiData[metric] || 0;
    }
    
    return 0;
  }

  // Extract AI Overview metrics from backend (Google Search AI Overview)
  getAIOverviewMetric(metric: 'ai_visibility' | 'mentions' | 'cited_pages' | 'monthly_audience' | 'industry_average'): number {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return 0;
    }
    const primaryBrand = this.llmComparisonData[0];
    
    // Use ai_visibility_metrics.platform_breakdown.ai_overview (new format)
    const aiOverviewData = primaryBrand?.ai_visibility_metrics?.platform_breakdown?.ai_overview;
    if (aiOverviewData !== undefined) {
      // Handle case where ai_overview is just a number (mentions count - legacy format)
      if (typeof aiOverviewData === 'number') {
        return metric === 'mentions' ? aiOverviewData : 0;
      }
      // Handle case where ai_overview is an object (new format)
      if (metric === 'ai_visibility') {
        return aiOverviewData.ai_visibility || 0;
      }
      return aiOverviewData[metric] || 0;
    }
    
    return 0;
  }

  // Extract AI Mode metrics from backend (Google AI Mode)
  getAIModeMetric(metric: 'ai_visibility' | 'mentions' | 'cited_pages' | 'monthly_audience' | 'industry_average'): number {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return 0;
    }
    const primaryBrand = this.llmComparisonData[0];
    
    // Use ai_visibility_metrics.platform_breakdown.ai_mode (new format)
    const aiModeData = primaryBrand?.ai_visibility_metrics?.platform_breakdown?.ai_mode;
    if (aiModeData !== undefined) {
      // Handle case where ai_mode is just a number (mentions count - legacy format)
      if (typeof aiModeData === 'number') {
        return metric === 'mentions' ? aiModeData : 0;
      }
      // Handle case where ai_mode is an object (new format)
      if (metric === 'ai_visibility') {
        return aiModeData.ai_visibility || 0;
      }
      return aiModeData[metric] || 0;
    }
    
    return 0;
  }

  /** True if this platform has data (backend omits failed platforms from platform_breakdown). */
  hasPlatformData(platform: 'chatgpt' | 'gemini' | 'ai_overview' | 'ai_mode'): boolean {
    if (!this.llmComparisonData?.length) return false;
    const pb = this.llmComparisonData[0]?.ai_visibility_metrics?.platform_breakdown;
    return !!pb?.[platform];
  }

  getOverallLLMMetrics(): { 
    aiVisibility: number; 
    mentions: number; 
    citedPages: number; 
    monthlyAudience: number; 
    industryAverage: number 
  } {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return { aiVisibility: 0, mentions: 0, citedPages: 0, monthlyAudience: 0, industryAverage: 0 };
    }

    const primaryBrand = this.llmComparisonData[0];
    
    // Use ai_visibility_metrics (new format)
    const metrics = primaryBrand?.ai_visibility_metrics;
    if (metrics) {
      // Get industry average from platform_breakdown (any platform has the same value)
      const industryAverage = metrics.platform_breakdown?.chatgpt?.industry_average || 
                              metrics.platform_breakdown?.gemini?.industry_average ||
                              metrics.platform_breakdown?.ai_overview?.industry_average ||
                              metrics.platform_breakdown?.ai_mode?.industry_average || 0;
      
      return {
        aiVisibility: metrics.ai_visibility_score || 0,
        mentions: metrics.mentions || 0,
        citedPages: metrics.cited_pages || 0,
        monthlyAudience: metrics.monthly_audience || 0,
        industryAverage: industryAverage
      };
    }

    return {
      aiVisibility: 0,
      mentions: 0,
      citedPages: 0,
      monthlyAudience: 0,
      industryAverage: 0
    };
  }

  getVisibilityCategory(score: number): string {
    if (score >= 80) return 'High';
    if (score >= 50) return 'Medium';
    return 'Low';
  }

  getVisibilityDescription(score: number): string {
    if (score >= 80) {
      return 'Frequently mentioned in LLM outputs with strong visibility across AI platforms.';
    } else if (score >= 50) {
      return 'Occasionally mentioned in LLM outputs, but Visibility can improve.';
    } else {
      return 'Rarely mentioned in LLM outputs. Consider improving AI visibility strategies.';
    }
  }

  formatNumber(value: number): string {
    if (value >= 1000000) {
      return Math.round(value / 1000000) + 'M';
    } else if (value >= 1000) {
      return Math.round(value / 1000) + 'K';
    }
    return Math.round(value).toString();
  }


  // Change calculations (placeholder - would need historical data)
  getMonthlyAudienceChange(): number {
    // TODO: Calculate from historical data
    return 0;
  }

  getMentionsChange(): number {
    // TODO: Calculate from historical data
    return 0;
  }

  getCitedPagesChange(): number {
    // TODO: Calculate from historical data
    return 0;
  }

  // Get explanations for metrics
  getExplanation(metric: 'monthly_audience' | 'total_mentions' | 'total_cited_pages' | 'chatgpt_mentions' | 'gemini_mentions' | 'ai_overview_mentions' | 'ai_mode_mentions'): string {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return '';
    }
    const primaryBrand = this.llmComparisonData[0];
    
    // Check brand-level explanations first
    if (primaryBrand?.explanations?.[metric]) {
      return primaryBrand.explanations[metric];
    }
    
    // Check model-level explanations from platform_breakdown
    if (metric === 'chatgpt_mentions' && primaryBrand?.ai_visibility_metrics?.platform_breakdown?.chatgpt?.explanations?.mentions) {
      return primaryBrand.ai_visibility_metrics.platform_breakdown.chatgpt.explanations.mentions;
    }
    if (metric === 'gemini_mentions' && primaryBrand?.ai_visibility_metrics?.platform_breakdown?.gemini?.explanations?.mentions) {
      return primaryBrand.ai_visibility_metrics.platform_breakdown.gemini.explanations.mentions;
    }
    if (metric === 'ai_overview_mentions' && primaryBrand?.ai_visibility_metrics?.platform_breakdown?.ai_overview?.explanations?.mentions) {
      return primaryBrand.ai_visibility_metrics.platform_breakdown.ai_overview.explanations.mentions;
    }
    if (metric === 'ai_mode_mentions' && primaryBrand?.ai_visibility_metrics?.platform_breakdown?.ai_mode?.explanations?.mentions) {
      return primaryBrand.ai_visibility_metrics.platform_breakdown.ai_mode.explanations.mentions;
    }
    
    return '';
  }

  getMetricExplanation(metric: 'mentions' | 'cited_pages' | 'monthly_audience', model?: 'chatgpt' | 'gemini' | 'ai_overview' | 'ai_mode'): string {
    if (!this.llmComparisonData || this.llmComparisonData.length === 0) {
      return '';
    }
    const primaryBrand = this.llmComparisonData[0];
    
    // Check model-level explanations from platform_breakdown
    if (model && primaryBrand?.ai_visibility_metrics?.platform_breakdown?.[model]?.explanations?.[metric]) {
      return primaryBrand.ai_visibility_metrics.platform_breakdown[model].explanations[metric];
    }
    
    // Fallback to brand-level explanations
    const explanationKey = model ? `${model}_${metric}` : `total_${metric}`;
    if (primaryBrand?.explanations?.[explanationKey]) {
      return primaryBrand.explanations[explanationKey];
    }
    
    return '';
  }

  // Handle clicks on scores to filter cited pages table
  onCitedPagesClick(): void {
    this.filterChange.emit('all');
  }

  onChatGPTClick(): void {
    this.filterChange.emit('chatgpt');
  }

  onGeminiClick(): void {
    this.filterChange.emit('gemini');
  }

  onAIOverviewClick(): void {
    this.filterChange.emit('ai_overview');
  }

  onAIModeClick(): void {
    this.filterChange.emit('ai_mode');
  }

  onStopClick(): void {
    if (this.llmComparisonMetricsTaskId) {
      if (confirm('Are you sure you want to stop the AI Visibility calculation? This action cannot be undone.')) {
        this.cancelTask.emit(this.llmComparisonMetricsTaskId);
      }
    }
  }

  onReloadClick(): void {
    if (confirm('Reload AI Visibility metrics? This will recalculate the metrics (takes 3-5 minutes).')) {
      this.reloadMetrics.emit();
    }
  }

  /** Status label: Pending, In Progress, Almost Completed */
  getStatusLabel(): string {
    if (!this.llmComparisonMetricsProgress) {
      return 'Pending';
    }
    const pct = this.llmComparisonMetricsProgress.progress_percentage || 0;
    const status = this.llmComparisonMetricsProgress.status || '';
    if (status === 'pending' || pct < 1) return 'Pending';
    if (pct >= 95 || status === 'completed') return 'Almost Completed';
    return 'In Progress';
  }

  /** Status detail: current working step text */
  getStatusDetailText(): string {
    const p = this.llmComparisonMetricsProgress;
    if (!p) return 'Pending';
    const current = p.current_tab || p.current_step || 'Processing...';
    const pct = p.progress_percentage || 0;
    if (pct >= 95) return `Almost Completed: ${current}`;
    if (pct > 0) return `In Progress: ${current}`;
    return 'Pending';
  }

  /** Recent status messages for loading sub-description (info/warning from query generation, validation, etc.) */
  getStatusMessages(): string[] {
    const p = this.llmComparisonMetricsProgress as { status_messages?: string[] } | null;
    return p?.status_messages ?? [];
  }
}
